import { useMemo } from 'react'

import {
  SelectableLocationsDocument,
  SelectableLocationsEmployeesDocument,
  SelectableLocationsQuery,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import concat from 'lodash/concat'
import uniq from 'lodash/uniq'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

const sortedLocations = (dataLocations: SelectableLocationsQuery) =>
  dataLocations.locations.locations
    .map(({ id, name }) => ({
      id,
      name,
    }))
    .sort(({ name: nameA }, { name: nameB }) => nameA.localeCompare(nameB))

export const useAllLocations = () => {
  const { data: dataLocations, loading } = useQuery(SelectableLocationsDocument, {
    variables: {
      employeeIds: [],
    },
  })

  return {
    loading,
    locations: dataLocations ? sortedLocations(dataLocations) : [],
  }
}

export const useSelectableLocations = (employeeId: string, showAll: boolean) => {
  const { allMyDescendants } = useCurrentSession()

  const { data, loading } = useQuery(SelectableLocationsEmployeesDocument, {
    variables: {
      employeeId: employeeId,
    },
    skip: showAll,
  })

  const hierarchyEmployeeIds = useMemo(() => {
    if (showAll) return []

    const reporteeIds = allMyDescendants

    const teamMemberIds = data?.employees?.employee
      ? compact(
          data.employees.employee.memberships
            .flatMap(({ team }) => team)
            .filter((team) => team?.leadIds && team?.leadIds.includes(employeeId))
            .flatMap((team) => team?.employeeIds)
        )
      : []

    return uniq(concat(reporteeIds, teamMemberIds))
  }, [allMyDescendants, data, employeeId, showAll])

  const { data: dataLocations, loading: loadingLocations } = useQuery(SelectableLocationsDocument, {
    variables: {
      employeeIds: hierarchyEmployeeIds,
    },
    skip: loading && !showAll,
  })

  const locations = useMemo(
    () => (dataLocations ? sortedLocations(dataLocations) : []),
    [dataLocations]
  )

  return {
    loading: loading || loadingLocations,
    locations,
  }
}
